// rfpBillingApi — wrapper around the shared axios `api` instance for RFP
// Manager's own billing endpoints (/api/v1/billing/rfp/*). `api` already
// injects the Bearer token and refreshes it on 401 (see pages/chat/api.js),
// so these stay thin. Same org-scoping convention as tendersApi.js.
//
// This covers only what the "Trial ended" view needs today (plans, the
// current subscription state, and starting a checkout) — renew/seats/
// cancel/resume/payment-method/invoices exist on the backend but have no
// frontend caller yet.
import Cookies from "js-cookie";

import api from "../pages/chat/api";

const BASE = "/api/v1/billing/rfp/";

const withOrg = (params = {}) => {
  const orgId = Cookies.get("selected_org_id");
  return orgId ? { organization_id: orgId, ...params } : { ...params };
};

// GET /api/v1/billing/rfp/plans/ -> { currency, unit_amount, seat_min, seat_max, admin_occupies_seat, trial_days }
export const fetchRfpPlans = async () => {
  const res = await api.get(`${BASE}plans/`, { params: withOrg() });
  return res.data;
};

// GET /api/v1/billing/rfp/subscription/
//   -> { state: "none"|"trialing"|"trial_expired"|"active"|"renewal_due"|"past_due"|"expired",
//        can_use, seats: { total, used, scheduled_total }, ... }
// Any member can call this, not just the admin — a locked workspace needs
// to show the right copy to everyone, not just whoever can pay.
export const fetchRfpSubscription = async () => {
  const res = await api.get(`${BASE}subscription/`, { params: withOrg() });
  return res.data;
};

// POST /api/v1/billing/rfp/checkout/ { seats, auto_renew, success_url, cancel_url }
//   -> { checkout_url, session_id }
// Admin only — the backend returns 403 for anyone else. Nothing is granted
// here; redirecting to checkout_url and completing payment is what a
// webhook later confirms (see the billing PRD's "two separate steps").
export const startRfpCheckout = async ({ seats, autoRenew = true, successUrl, cancelUrl }) => {
  const res = await api.post(
    `${BASE}checkout/`,
    { seats, auto_renew: autoRenew, success_url: successUrl, cancel_url: cancelUrl },
    { params: withOrg() }
  );
  return res.data;
};

// GET /api/v1/billing/rfp/invoices/ -> { invoices: [{ number, kind, period_start,
//   period_end, seat_quantity, currency, total, status, pdf_url, paid_at, created_at }] }
export const fetchRfpInvoices = async () => {
  const res = await api.get(`${BASE}invoices/`, { params: withOrg() });
  return res.data;
};

// GET /api/v1/billing/rfp/invoices/<number>/pdf/ -> { pdf_url }
// Looked up from Stripe on click; a 404 means Stripe has no PDF for it.
export const fetchRfpInvoicePdfUrl = async (number) => {
  const res = await api.get(`${BASE}invoices/${encodeURIComponent(number)}/pdf/`, { params: withOrg() });
  return res.data.pdf_url;
};

// POST /api/v1/billing/rfp/seats/ { seats, success_url?, cancel_url? }
//   Two shapes back:
//   - increasing (auto-pay or not) -> { checkout_url, session_id, amount }  (redirect to pay the added seats, prorated to the period end)
//   - decreasing                   -> { scheduled_seat_quantity, effective_at }  (no payment, takes effect at renewal)
export const changeRfpSeats = async ({ seats, successUrl, cancelUrl }) => {
  const res = await api.post(
    `${BASE}seats/`,
    { seats, success_url: successUrl, cancel_url: cancelUrl },
    { params: withOrg() }
  );
  return res.data;
};

// POST /api/v1/billing/rfp/cancel/ -> { cancel_at_period_end: true, period_end }
// A real cancellation (T14) — distinct from turning auto-pay off below.
export const cancelRfpPlan = async () => {
  const res = await api.post(`${BASE}cancel/`, {}, { params: withOrg() });
  return res.data;
};

// POST /api/v1/billing/rfp/resume/ -> { cancel_at_period_end: false }
export const resumeRfpPlan = async () => {
  const res = await api.post(`${BASE}resume/`, {}, { params: withOrg() });
  return res.data;
};

// POST /api/v1/billing/rfp/renew/ { seats?, success_url, cancel_url } -> { checkout_url }
// Manual renewal from renewal_due. `seats` omitted keeps the current count.
export const renewRfpSubscription = async ({ seats, successUrl, cancelUrl } = {}) => {
  const res = await api.post(
    `${BASE}renew/`,
    { seats: seats ?? null, success_url: successUrl, cancel_url: cancelUrl },
    { params: withOrg() }
  );
  return res.data;
};

// POST /api/v1/billing/rfp/auto-renew/ { enabled, success_url?, cancel_url? }
//   enabled: true  -> { auto_renew: true } when Stripe still has the subscription (switched back on), else
//                     { checkout_url, session_id }  (sets up the card, charges nothing today)
//   enabled: false -> { auto_renew: false }
export const setRfpAutoRenew = async ({ enabled, successUrl, cancelUrl }) => {
  const res = await api.post(
    `${BASE}auto-renew/`,
    { enabled, success_url: successUrl, cancel_url: cancelUrl },
    { params: withOrg() }
  );
  return res.data;
};

// POST /api/v1/billing/rfp/payment-method/ { return_url } -> { portal_url }
export const openRfpPaymentPortal = async (returnUrl) => {
  const res = await api.post(`${BASE}payment-method/`, { return_url: returnUrl }, { params: withOrg() });
  return res.data;
};

// POST /api/v1/billing/rfp/retry-payment/ -> { paid: true }
// Charges the card on file again for a past_due workspace's open invoice.
// A decline comes back as an error whose message is safe to show the user.
export const retryRfpPayment = async () => {
  const res = await api.post(`${BASE}retry-payment/`, {}, { params: withOrg() });
  return res.data;
};

// GET /api/v1/billing/rfp/quota/?kind=projects|kb_documents|tender_insights&adding=N
// Returns the message to show if the workspace can't add more, else null. Asked
// BEFORE a slow upload so the user isn't refused after waiting for it; the real
// request still enforces the limit, so a failed check here never blocks anything.
export const quotaBlockedMessage = async (kind, adding = 1) => {
  try {
    const res = await api.get(`${BASE}quota/`, { params: withOrg({ kind, adding }) });
    return res.data.allowed ? null : res.data.message;
  } catch {
    return null;
  }
};

// Same numbers as rfp/billing/catalog.py (SEAT_MAX, PROJECT_LIMIT_PER_SEAT_PAID): 11+ seats is
// Enterprise, sold by talking to us rather than self-serve.
export const SEAT_MAX = 10;
export const PROJECTS_PER_SEAT = 10;
// Where "Talk to sales" goes: a booking page, for teams that need more than the self-serve seat limit.
export const SALES_URL = "https://app.lemcal.com/@shobhitg/gen-ai-solution";

// States where Stripe has actually confirmed a payment. Not the same as
// `can_use` — a trial is usable too.
export const PAID_STATES = new Set(["active", "renewal_due", "past_due"]);

export const formatMoney = (cents, currency = "USD", wholeDollars = true) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency, ...(wholeDollars && { maximumFractionDigits: 0 }) }).format(
    cents / 100
  );

export const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "—";
